import React, { useEffect, useRef, useState } from "react";
import ObjectInputService from "../../../../../services/ObjectInput";
import useObjectInput from "../../../../../hooks/useObjectInput";
import { getIsTempFileSaved } from "./fileApi";
import { scrollToTop } from "../../../../../utils";
import RenderInputField from "./RenderInputField";
import RenderUploadFile from "./RenderUploadFile";
import "./index.css";

const GOIFieldFile = ({ disabled, value, inputData }) => {
  const fileInputRef = useRef(null);

  let intervalId;

  const { id, options: fileOptions } = inputData;

  const {
    uploadingData,
    setUploading,
    reqResponse,
    findObjectById,
    changeShowErrorToast,
    handleOtherErrors,
    showErrors,
    onInputValueChange,
  } = useObjectInput();

  const uploading = uploadingData[id] || {
     isFile: false,
     uploadProgress: 0,
     uploading: false,
     isSaved: false,
     verifying: false,
    };

  const handleValueChange = (e) => {
    let inputValue;
    inputValue = e.target
      ? { name: e.target.value.name, size: e.target.value.size }
      : { name: e[0].name, size: e[0].size };
    onInputValueChange(id, inputValue);
  };

  const [fileData, setFileData] = useState();
  const [localValue, setLocalvalue] = useState(value);

  const fileTypes = fileOptions ? fileOptions.acceptedExtensions?.join(",") : [];

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleDownload = async () => {
    try {
      const res = await ObjectInputService.getFileDownlaodURL({
        id,
        isTmp: !uploading.isSaved,
      });

      if (res) {
        const blob = await fetch(res.result).then((response) => response.blob());
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = localValue.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch {}
  };

  const handleRemove = () => {
    if (!disabled) {
      setLocalvalue();
      setUploading(id, { isSaved: false });
    }
  };

  const handleCancel = () => {
    if (!disabled) {
      setLocalvalue();
      setUploading(id, { verifying: false });
      clearInterval(intervalId);
    }
  };

  // gets called when the user selects or drops the file
  const handleChange = (files) => {
    const fileSizeTooBig = fileOptions.acceptedMaxSize ? files[0].size > fileOptions.acceptedMaxSize : false;
    if (fileSizeTooBig) {
      alert("File Size is too big");
      setLocalvalue();
    } else {
      setUploading(id, { isFile: true,  verifying: true });
      setLocalvalue({
        name: files[0].name,
        size: files[0].size,
        type: files[0].type,
      });
      handleValueChange(files);
  
      getSignedUrl(files);
    }
  };

  // get aws signed url for file upload
  const getSignedUrl = async (files) => {
    try {
      const response = await ObjectInputService.getFileUploadURL({
        id,
        name: files[0].name,
      });

      if (response.error) {
        handleOtherErrors(response);
        if (!showErrors) {
          changeShowErrorToast(true);
        }
        scrollToTop();
      } else {
        uploadFile(files, response.result);
      }
    } catch (error) {
      console.error("Error getting signed URL:", error);
    }
  };

  // to upload the file directly to aws s3
  const uploadFile = async (files, url) => {
    if (!url) {
      console.error("No signed URL available.");
      return;
    }
    try {
      await getIsTempFileSaved(
        id,
        files,
        setUploading,
        url,
        setLocalvalue,
        changeShowErrorToast,
        handleOtherErrors,
        showErrors,
        intervalId
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    handleChange(files);
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    handleChange(files);
  };

  useEffect(() => {
    if (value) {
      setLocalvalue(value);
      setUploading(id, { isSaved: true });
    } else {
      if (fileData)
        setLocalvalue({
          name: fileData.rtOptions.tmpSavedValue.name,
          size: fileData.rtOptions.tmpSavedValue.size,
          type: `.${fileData.rtOptions.tmpSavedValue.name.split(".")[1]}`,
        });
    }
    // eslint-disable-next-line
  }, [value]);

  useEffect(() => {
    if (reqResponse.data) {
      setFileData(findObjectById(id));
    }
    // eslint-disable-next-line
  }, [reqResponse]);

  return localValue ? (
    <>
      <RenderUploadFile
        localValue={localValue}
        disabled={disabled}
        handleRemove={handleRemove}
        handleDownload={handleDownload}
        uploadFile={uploadFile}
        handleCancel={handleCancel}
        id={id}
      />
      {!uploading.isSaved && (
        <p className="goi-field-file-message">
          This file is not saved, it will be deleted 7 days after it was
          uploaded
        </p>
      )}
    </>
  ) : (
    <RenderInputField
      fileTypes={fileTypes}
      fileInputRef={fileInputRef}
      handleFileSelect={handleFileSelect}
      disabled={disabled}
      handleDragOver={handleDragOver}
      handleDrop={handleDrop}
      handleClick={handleClick}
      fileOptions={fileOptions}
    />
  );
};

export default GOIFieldFile;
